///
/// EarthBrowser
///
/// Created by Matt Giger on 11/20/2023
/// Copyright (c) 2023 Geodata Labs
///

import { type RenderActionSet, type Renderer } from "@stovokor/engine/Renderer"
import { requestRender } from "@stovokor/engine/RenderRequest"
import { NodeMatrixCache, type RenderState } from "@stovokor/engine/RenderState"
import { Transform } from "@stovokor/engine/Transform"
import type { GestureNode } from "@stovokor/gestures/GestureNode"
import type { GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { vec3 } from "@stovokor/math/vector"

// Shared immutable empty result so nodes with nothing to draw allocate nothing
const cNoActionSets: RenderActionSet[] = []

export class NodeBase {
	public visible: boolean = true

	/**
	 * Primary draw-order key within a viewport pass; lower draws first and
	 * `depth` only orders nodes sharing the same value. Default 0. Nodes
	 * that must draw behind everything regardless of distance (the sky,
	 * which disables the depth test) use negative values.
	 */
	public renderOrder: number = 0

	protected _children: NodeBase[] = []
	protected _transform = new Transform()
	/** World-space position of the node origin, for globalToLocal */
	protected _globalOffset: vec3 = vec3.zero
	private _offsetSource?: matrix
	/** View-space depth of the node origin, see `depth` */
	protected _viewDepth: number = 0
	/** Memoized matrix products for pushTransform, see NodeMatrixCache */
	protected readonly matrixCache = new NodeMatrixCache()
	protected renderer?: Renderer

	public get children(): NodeBase[] {
		return this._children
	}

	public addNode<T extends NodeBase>(child: T | T[]): T {
		let ret: T = child as T
		if(Array.isArray(child)) {
			this._children.push(...child)
			ret = child[child.length - 1]
		} else {
			this._children.push(child)
		}
		requestRender()
		return ret
	}

	public removeNode(child: NodeBase | NodeBase[] | undefined): void {
		if(child === undefined) {
			return
		} else if(Array.isArray(child)) {
			this._children = this._children.filter(c => !child.includes(c))
		} else {
			this._children = this._children.filter(c => c !== child)
		}
		requestRender()
	}

	public removeAllNodes(): NodeBase {
		this._children = []
		requestRender()
		return this
	}

	public destroy(): void {
		this._children.forEach(child => child.destroy())
		this._children = []
		this.renderer?.destroy()
		this.renderer = undefined
	}

	public get transform(): matrix {
		return this._transform.transform
	}
	public set transform(value: matrix) {
		this._transform.transform = value
	}
	public globalToLocal(globalPoint: vec3): vec3 {
		return globalPoint.subtract(this._globalOffset)
	}
	/**
	 * Distance of the node origin in front of the camera along the view
	 * axis, from the last render. The world viewport draws opaque nodes
	 * near-to-far and transparent nodes far-to-near by this. (It used to
	 * return the world z coordinate, which only matched view depth for a
	 * camera on the +z axis looking at the origin.)
	 */
	public get depth(): number {
		return this._viewDepth
	}
	public get worldPosition(): vec3 {
		return this._globalOffset
	}

	public parentResized(_width: number, _height: number, _deltaWidth: number, _deltaHeight: number): void {
		// do nothing, this is for Pane children to override
	}

	public render(state: RenderState): RenderActionSet[] {
		if(!this.visible) {
			return cNoActionSets
		}

		const model = state.pushTransform(this.transform, this.matrixCache)
		if(this.matrixCache.model !== this._offsetSource) {
			// World position only changes with the model matrix
			this._globalOffset = model.position
			this._offsetSource = model
		}
		this._viewDepth = state.viewDepth

		let nodeAction: RenderActionSet[] = cNoActionSets

		if(this.renderer) {
			const xsphere = state.viewSphere(this.renderer.geometry.boundingSphere, this.matrixCache)
			if(state.frustum.intersects(xsphere) !== "outside") {
				nodeAction = this.renderActions(state)
			}
		}

		// Avoid building a merged array when one side is empty, the common
		// case for both leaf nodes and pure group nodes
		const children = this._children.length > 0 ? this.renderChildren(state) : cNoActionSets
		let actions: RenderActionSet[]
		if(children.length === 0) {
			actions = nodeAction
		} else if(nodeAction.length === 0) {
			actions = children
		} else {
			actions = nodeAction.concat(children)
		}

		state.popTransform()

		return actions
	}

	protected renderChildren(state: RenderState): RenderActionSet[] {
		// One result array filled in place rather than flatMap's per-child
		// intermediates; a single child's array passes through untouched
		const children = this._children
		if(children.length === 1) {
			return children[0].render(state)
		}
		const out: RenderActionSet[] = []
		for(let i = 0; i < children.length; i++) {
			const sets = children[i].render(state)
			for(let j = 0; j < sets.length; j++) {
				out.push(sets[j])
			}
		}
		return out
	}

	public renderActions(state: RenderState): RenderActionSet[] {
		return this.renderer ? [this.renderer.render(state, this.depth, this.renderOrder)] : cNoActionSets
	}

	/**
	 * Hit test for the gesture system - doesn't require event interest
	 * This allows gesture recognition to work with any visible node that has geometry,
	 * regardless of whether it's set up for the legacy event system.
	 */
	public gestureHitTest(state: RenderState): GestureHitResult[] {
		if(!this.visible) {
			return []
		}

		state.pushRay(state.ray.multMatrix(this._transform.inverse))

		const hits = this._children.flatMap(c => c.gestureHitTest(state))

		// Duck-type check to avoid circular dependency with GestureNode
		const isGestureNode = "recognizers" in this && "focusable" in this
		if(isGestureNode) {
			const hit = this.gestureIntersection(state)
			if(hit) {
				hits.push(hit)
			}
		}

		state.popRay()

		return hits
	}

	public gestureIntersection(state: RenderState): GestureHitResult | undefined {
		if(!this.renderer) {
			return undefined
		}

		const dist = this.renderer.geometry.bounds.intersect(state.ray)
		if(dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this as unknown as GestureNode, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}

	public calculateBoundingBox(): cube {
		const bounds = new cube()
		if(this.renderer) {
			bounds.add(this.renderer.geometry.bounds)
		}

		for(const child of this._children) {
			const childBounds = child.calculateBoundingBox()
			childBounds.apply(child.transform)
			bounds.add(childBounds)
		}

		return bounds
	}
}
